import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { AdminPanelButton } from './AdminPanelButton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs'
import { Search, RefreshCw, Eye, DollarSign, X, BarChart3, Table2 } from 'lucide-react'
import { API_BASE_URL } from '../../../lib/config'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table'
import { formatISTDate } from '../../../components/lib/dateUtils'
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'

/** Stored provider id on assistant messages; legacy rows show as "unknown". */
const MODEL_LABEL: Record<string, string> = {
  unknown: 'Unknown (legacy)',
  gpt_4o: 'GPT-4o',
  gpt_5_1: 'GPT-5.1',
  claude_sonnet_4_5: 'Claude Sonnet 4.5',
  claude_sonnet_4_6: 'Claude Sonnet 4.6',
  claude_sonnet_5: 'Claude Sonnet 5',
}

function modelLabel(id: string) {
  return MODEL_LABEL[id] ?? id
}

/** ISO date yyyy-mm-dd → dd/mm/yyyy for display */
function isoDateToDdMmYyyy(iso: string): string {
  const d = iso.slice(0, 10)
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return ''
  return `${m[3]}/${m[2]}/${m[1]}`
}

/** Parse dd/mm/yyyy (day first) to yyyy-mm-dd for API (calendar validation). Dates are interpreted as IST on the server. */
function parseDdMmYyyyToIso(s: string): string | null {
  const t = s.trim()
  if (!t) return null
  const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (!m) return null
  const day = Number(m[1])
  const month = Number(m[2])
  const year = Number(m[3])
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  const dt = new Date(Date.UTC(year, month - 1, day))
  if (dt.getUTCFullYear() !== year || dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day) return null
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/** yyyy-mm-dd in Asia/Kolkata for an instant (for presets). Uses formatToParts so locale layout cannot break parsing. */
function formatYmdInIst(d: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d)
  const y = parts.find((p) => p.type === 'year')?.value
  const m = parts.find((p) => p.type === 'month')?.value
  const day = parts.find((p) => p.type === 'day')?.value
  if (!y || !m || !day) return ''
  return `${y}-${m}-${day}`
}

interface SessionTokenUsage {
  session_id: string
  session_name: string
  created_at: string
  total_input_tokens: number
  total_output_tokens: number
  total_cost: number
}

interface TokenMetrics {
  generated_at_ist: string
  /** When set, all metrics use this IST calendar window (including overview). */
  breakdown_range_ist: { from: string | null; to: string | null } | null
  core: {
    total_input_tokens: number
    total_output_tokens: number
    total_tokens: number
    tracked_message_count: number
    distinct_sessions_with_tokens: number
    distinct_users_with_tokens: number
    tokens_per_message: number | null
    tokens_per_session: number | null
    tokens_per_user_daily_window: number | null
    tokens_per_user_month_window: number | null
  }
  cost: {
    total_cost: number | null
    cost_per_message: number | null
    cost_per_session: number | null
    cost_per_user_daily_window: number | null
    cost_per_user_month_window: number | null
    effective_cost_per_1k_tokens: number | null
  }
  usage_windows: {
    today_ist: {
      total_tokens: number
      total_cost: number | null
      active_users: number
      tracked_messages: number
      tokens_per_user: number | null
      cost_per_user: number | null
    }
    month_to_date_ist: {
      total_tokens: number
      total_cost: number | null
      active_users: number
      tracked_messages: number
      tokens_per_user: number | null
      cost_per_user: number | null
    }
  }
  session_metrics: {
    avg_tokens_per_session: number | null
    avg_cost_per_session: number | null
  }
  message_metrics_global: {
    avg_tokens_per_message: number | null
    avg_cost_per_message: number | null
    input_vs_output_ratio: number | null
  }
  by_model: Array<{
    model: string
    input_tokens: number
    output_tokens: number
    total_tokens: number
    total_cost: number | null
    message_count: number
    cost_per_1k_tokens: number | null
  }>
  by_user_model: Array<{
    user_id: string
    email: string
    username: string
    first_name?: string
    last_name?: string
    model: string
    input_tokens: number
    output_tokens: number
    total_tokens: number
    total_cost: number | null
    messages: number
    sessions: number
    avg_tokens_per_message: number | null
    avg_cost_per_message: number | null
    cost_per_1k_tokens: number | null
  }>
  users: Array<{
    user_id: string
    email: string
    username: string
    first_name?: string
    last_name?: string
    total_input_tokens: number
    total_output_tokens: number
    total_cost: number | null
    message_count: number
    session_count: number
    avg_tokens_per_message: number | null
    avg_cost_per_message: number | null
    input_vs_output_ratio: number | null
    tokens_today: number | null
    cost_today: number | null
    tokens_this_month: number | null
    cost_this_month: number | null
  }>
  time: {
    daily: Array<{ date: string; tokens: number; cost: number | null; messages: number }>
    weekly: Array<{ year: number; week: number; tokens: number; cost: number | null; messages: number }>
    monthly: Array<{ year: number; month: number; tokens: number; cost: number | null; messages: number }>
    hourly_distribution: Array<{ hour: number; tokens: number }>
    by_day_of_week: Array<{ day_of_week: number; label: string; tokens: number }>
    peak_hour: { hour: number; tokens: number } | null
    peak_day_of_week: { day_of_week: number; label: string; tokens: number } | null
  }
}

export function TokenUsage() {
  const [metrics, setMetrics] = useState<TokenMetrics | null>(null)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  /** YYYY-MM-DD for API (breakdown_from / breakdown_to). */
  const [breakdownFrom, setBreakdownFrom] = useState('')
  const [breakdownTo, setBreakdownTo] = useState('')
  /** User-facing dd/mm/yyyy in Overview date row */
  const [dateFromDdMm, setDateFromDdMm] = useState('')
  const [dateToDdMm, setDateToDdMm] = useState('')
  const [dateRangeError, setDateRangeError] = useState<string | null>(null)
  const [userModelFilter, setUserModelFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(null)
  const [sessions, setSessions] = useState<SessionTokenUsage[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [sessionsDialogOpen, setSessionsDialogOpen] = useState(false)

  useEffect(() => {
    if (sessionsDialogOpen) {
      const originalOverflow = document.body.style.overflow
      const originalPaddingRight = document.body.style.paddingRight
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
      document.body.style.overflow = 'hidden'
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`
      }
      return () => {
        document.body.style.overflow = originalOverflow
        document.body.style.paddingRight = originalPaddingRight
      }
    }
  }, [sessionsDialogOpen])

  useEffect(() => {
    void fetchMetrics()
  }, [])

  useEffect(() => {
    const br = metrics?.breakdown_range_ist
    if (!br) return
    if (br.from && /^\d{4}-\d{2}-\d{2}$/.test(br.from.slice(0, 10))) {
      const fromIso = br.from.slice(0, 10)
      setBreakdownFrom(fromIso)
      setDateFromDdMm(isoDateToDdMmYyyy(fromIso))
    } else if (br.from === null) {
      setBreakdownFrom('')
      setDateFromDdMm('')
    }
    if (br.to && /^\d{4}-\d{2}-\d{2}$/.test(br.to.slice(0, 10))) {
      const toIso = br.to.slice(0, 10)
      setBreakdownTo(toIso)
      setDateToDdMm(isoDateToDdMmYyyy(toIso))
    } else if (br.to === null) {
      setBreakdownTo('')
      setDateToDdMm('')
    }
  }, [metrics?.breakdown_range_ist?.from, metrics?.breakdown_range_ist?.to])

  const fetchMetrics = async (opts?: { breakdownFrom?: string; breakdownTo?: string }) => {
    const isOpts =
      opts != null &&
      typeof opts === 'object' &&
      ('breakdownFrom' in opts || 'breakdownTo' in opts)
    const bf = isOpts && opts.breakdownFrom !== undefined ? opts.breakdownFrom : breakdownFrom
    const bt = isOpts && opts.breakdownTo !== undefined ? opts.breakdownTo : breakdownTo
    setLoading(true)
    setFetchError(null)
    try {
      const params = new URLSearchParams()
      if (bf) params.set('breakdown_from', bf)
      if (bt) params.set('breakdown_to', bt)
      const qs = params.toString()
      const response = await fetch(
        `${API_BASE_URL}admin/token-usage/metrics${qs ? `?${qs}` : ''}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      )
      const text = await response.text()
      let body: { error?: string; detail?: string } | null = null
      try {
        body = text ? JSON.parse(text) : null
      } catch {
        body = null
      }
      if (response.ok) {
        setMetrics(body as TokenMetrics)
        return
      }
      const msg =
        (body && typeof body === 'object' && 'error' in body && body.error) ||
        (body && typeof body === 'object' && 'detail' in body && String(body.detail)) ||
        text?.slice(0, 500) ||
        `HTTP ${response.status}`
      setFetchError(typeof msg === 'string' ? msg : JSON.stringify(msg))
      console.error('Failed to fetch token metrics:', response.status, msg)
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      setFetchError(msg)
      console.error('Error fetching token metrics:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchUserSessions = async (userId: string, email: string) => {
    setSelectedUserEmail(email)
    setSessionsLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}admin/token-usage/user/${userId}/sessions`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response.ok) {
        const data = await response.json()
        setSessions(data.sessions || [])
        setSessionsDialogOpen(true)
      }
    } catch (error) {
      console.error('Error fetching user sessions:', error)
    } finally {
      setSessionsLoading(false)
    }
  }

  const formatCurrency = (value: number | null | undefined) => {
    if (value == null || !Number.isFinite(value)) return '—'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 6,
      maximumFractionDigits: 6,
    }).format(value)
  }

  const formatNumber = (value: number | null | undefined) => {
    if (value == null || !Number.isFinite(value)) return '—'
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value)
  }

  const formatInt = (value: number | null | undefined) => {
    if (value == null || !Number.isFinite(value)) return '—'
    return new Intl.NumberFormat('en-US').format(Math.round(value))
  }

  const users = metrics?.users ?? []
  const filteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.first_name && user.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.last_name && user.last_name.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const core = metrics?.core
  const cost = metrics?.cost
  const sessM = metrics?.session_metrics

  const userModelIds = useMemo(() => {
    const ids = new Set<string>()
    for (const row of metrics?.by_user_model ?? []) {
      ids.add(row.model)
    }
    return Array.from(ids).sort()
  }, [metrics?.by_user_model])

  const filteredByUserModel = useMemo(() => {
    const rows = metrics?.by_user_model ?? []
    if (userModelFilter === 'all') return rows
    return rows.filter((r) => r.model === userModelFilter)
  }, [metrics?.by_user_model, userModelFilter])

  useEffect(() => {
    if (userModelFilter !== 'all' && !userModelIds.includes(userModelFilter)) {
      setUserModelFilter('all')
    }
  }, [userModelIds, userModelFilter])

  /** Radix Select throws if value is not present in items — keep in sync with options. */
  const userModelSelectValue =
    userModelFilter === 'all' || userModelIds.includes(userModelFilter) ? userModelFilter : 'all'

  const userTableColSpan = 9

  const applyPresetLast30Days = () => {
    setDateRangeError(null)
    const ts = formatYmdInIst(new Date())
    const ago = new Date(Date.now() - 30 * 86400000 * 1000)
    const fs = formatYmdInIst(ago)
    setBreakdownFrom(fs)
    setBreakdownTo(ts)
    setDateFromDdMm(isoDateToDdMmYyyy(fs))
    setDateToDdMm(isoDateToDdMmYyyy(ts))
    void fetchMetrics({ breakdownFrom: fs, breakdownTo: ts })
  }

  const clearBreakdownFilter = () => {
    setDateRangeError(null)
    setBreakdownFrom('')
    setBreakdownTo('')
    setDateFromDdMm('')
    setDateToDdMm('')
    void fetchMetrics({ breakdownFrom: '', breakdownTo: '' })
  }

  const applyDateRange = () => {
    setDateRangeError(null)
    const fromRaw = dateFromDdMm.trim()
    const toRaw = dateToDdMm.trim()
    if (!fromRaw && !toRaw) {
      clearBreakdownFilter()
      return
    }
    const fromIso = fromRaw ? parseDdMmYyyyToIso(fromRaw) : null
    const toIso = toRaw ? parseDdMmYyyyToIso(toRaw) : null
    if (fromRaw && !fromIso) {
      setDateRangeError('Invalid From date. Use dd/mm/yyyy.')
      return
    }
    if (toRaw && !toIso) {
      setDateRangeError('Invalid To date. Use dd/mm/yyyy.')
      return
    }
    const bf = fromIso ?? ''
    const bt = toIso ?? ''
    if (bf && bt && bf > bt) {
      setDateRangeError('From must be on or before To.')
      return
    }
    setBreakdownFrom(bf)
    setBreakdownTo(bt)
    void fetchMetrics({ breakdownFrom: bf, breakdownTo: bt })
  }

  return (
    <div className="space-y-6">
      {fetchError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load token metrics</AlertTitle>
          <AlertDescription className="font-mono text-xs whitespace-pre-wrap break-words">
            {fetchError}
          </AlertDescription>
        </Alert>
      )}
      {metrics && !fetchError && (metrics.core?.tracked_message_count ?? 0) === 0 && (
        <Alert>
          <AlertTitle>{metrics.breakdown_range_ist ? 'No data in this range' : 'No tracked usage yet'}</AlertTitle>
          <AlertDescription>
            {metrics.breakdown_range_ist
              ? 'Widen the date range or clear it to see more results.'
              : 'There are no assistant messages with both input and output token counts recorded for this deployment.'}
          </AlertDescription>
        </Alert>
      )}
      <div className="flex justify-end">
        <AdminPanelButton type="button" onClick={() => void fetchMetrics()} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </AdminPanelButton>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Date range</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="space-y-2">
            <Label htmlFor="ov-from" className="font-mono text-xs text-muted-foreground">
              From (dd/mm/yyyy)
            </Label>
            <Input
              id="ov-from"
              type="text"
              inputMode="numeric"
              placeholder="dd/mm/yyyy"
              autoComplete="off"
              value={dateFromDdMm}
              onChange={(e) => setDateFromDdMm(e.target.value)}
              className="w-[140px] font-mono"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ov-to" className="font-mono text-xs text-muted-foreground">
              To (dd/mm/yyyy)
            </Label>
            <Input
              id="ov-to"
              type="text"
              inputMode="numeric"
              placeholder="dd/mm/yyyy"
              autoComplete="off"
              value={dateToDdMm}
              onChange={(e) => setDateToDdMm(e.target.value)}
              className="w-[140px] font-mono"
            />
          </div>
          <AdminPanelButton type="button" onClick={applyDateRange} disabled={loading}>
            Apply range
          </AdminPanelButton>
          <AdminPanelButton type="button" onClick={applyPresetLast30Days} disabled={loading}>
            Last 30 days
          </AdminPanelButton>
          <AdminPanelButton type="button" onClick={clearBreakdownFilter} disabled={loading}>
            Clear (all time)
          </AdminPanelButton>
          {dateRangeError && <p className="text-sm text-destructive sm:w-full">{dateRangeError}</p>}
          {metrics?.breakdown_range_ist && !dateRangeError && (
            <p className="text-sm text-muted-foreground sm:w-full">
              Active:{' '}
              {metrics.breakdown_range_ist.from
                ? isoDateToDdMmYyyy(metrics.breakdown_range_ist.from.slice(0, 10))
                : '—'}{' '}
              →{' '}
              {metrics.breakdown_range_ist.to
                ? isoDateToDdMmYyyy(metrics.breakdown_range_ist.to.slice(0, 10))
                : '—'}
            </p>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="overview" className="gap-1">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="breakdown" className="gap-1">
            <Table2 className="h-4 w-4" />
            Models & users
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-3">Core usage</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Total input tokens</CardTitle>
                </CardHeader>
                <CardContent className="text-2xl font-bold font-mono">
                  {formatInt(core?.total_input_tokens)}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Total output tokens</CardTitle>
                </CardHeader>
                <CardContent className="text-2xl font-bold font-mono">
                  {formatInt(core?.total_output_tokens)}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Total tokens</CardTitle>
                </CardHeader>
                <CardContent className="text-2xl font-bold font-mono">
                  {formatInt(core?.total_tokens)}
                </CardContent>
              </Card>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-3">Cost tracking</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-medium">Total cost</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent className="text-2xl font-bold">{formatCurrency(cost?.total_cost)}</CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Cost per message</CardTitle>
                </CardHeader>
                <CardContent className="text-2xl font-bold">{formatCurrency(cost?.cost_per_message)}</CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Cost per session</CardTitle>
                </CardHeader>
                <CardContent className="text-2xl font-bold">{formatCurrency(cost?.cost_per_session)}</CardContent>
              </Card>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-3">Session & message (global)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Avg tokens per session</CardTitle>
                </CardHeader>
                <CardContent className="text-xl font-mono font-semibold">
                  {formatNumber(sessM?.avg_tokens_per_session)}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Avg cost per session</CardTitle>
                </CardHeader>
                <CardContent className="text-xl font-semibold">{formatCurrency(sessM?.avg_cost_per_session)}</CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="breakdown" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Model-wise tracking</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {loading && !metrics ? (
                <p className="text-muted-foreground py-6">Loading…</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Model</TableHead>
                      <TableHead className="text-right">Input</TableHead>
                      <TableHead className="text-right">Output</TableHead>
                      <TableHead className="text-right">Total tokens</TableHead>
                      <TableHead className="text-right">Messages</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead className="text-right">Cost / 1K</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(metrics?.by_model ?? []).map((row) => (
                      <TableRow key={row.model}>
                        <TableCell className="font-medium">{modelLabel(row.model)}</TableCell>
                        <TableCell className="text-right font-mono">{formatInt(row.input_tokens)}</TableCell>
                        <TableCell className="text-right font-mono">{formatInt(row.output_tokens)}</TableCell>
                        <TableCell className="text-right font-mono">{formatInt(row.total_tokens)}</TableCell>
                        <TableCell className="text-right font-mono">{formatInt(row.message_count)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.total_cost)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.cost_per_1k_tokens)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>User-wise model usage</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <Label htmlFor="um-model-filter" className="whitespace-nowrap">
                  Model
                </Label>
                <Select value={userModelSelectValue} onValueChange={setUserModelFilter}>
                  <SelectTrigger id="um-model-filter" className="w-full sm:w-[260px]">
                    <SelectValue placeholder="All models" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All models</SelectItem>
                    {userModelIds.map((id) => (
                      <SelectItem key={id} value={id}>
                        {modelLabel(id)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead className="text-right">Total tok</TableHead>
                      <TableHead className="text-right">Msgs</TableHead>
                      <TableHead className="text-right">Sess</TableHead>
                      <TableHead className="text-right">Avg tok/msg</TableHead>
                      <TableHead className="text-right">Avg $/msg</TableHead>
                      <TableHead className="text-right">$/1K</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredByUserModel.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                          {userModelFilter === 'all' ? 'No data.' : 'No rows for this model.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredByUserModel.map((row, i) => (
                      <TableRow key={`${row.user_id}-${row.model}-${i}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{row.email}</p>
                            <p className="text-xs text-muted-foreground">{row.username}</p>
                          </div>
                        </TableCell>
                        <TableCell>{modelLabel(row.model)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatInt(row.total_tokens)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatInt(row.messages)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatInt(row.sessions)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatNumber(row.avg_tokens_per_message)}</TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(row.avg_cost_per_message)}</TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(row.cost_per_1k_tokens)}</TableCell>
                        <TableCell className="text-right text-sm font-semibold">{formatCurrency(row.total_cost)}</TableCell>
                      </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Totals by user</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Label htmlFor="search">Search users</Label>
                  <div className="relative mt-2">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input
                      id="search"
                      placeholder="Email, username, or name…"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead className="text-right">Msgs</TableHead>
                      <TableHead className="text-right">Sess</TableHead>
                      <TableHead className="text-right">In</TableHead>
                      <TableHead className="text-right">Out</TableHead>
                      <TableHead className="text-right">Avg tok/msg</TableHead>
                      <TableHead className="text-right">Avg $/msg</TableHead>
                      <TableHead className="text-right">Total $</TableHead>
                      <TableHead className="text-center">View</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && !metrics ? (
                      <TableRow>
                        <TableCell colSpan={userTableColSpan} className="text-center text-muted-foreground py-8">
                          Loading…
                        </TableCell>
                      </TableRow>
                    ) : filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={userTableColSpan} className="text-center text-muted-foreground py-8">
                          {searchTerm ? 'No matches.' : 'No token data.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map((user) => (
                        <TableRow key={user.user_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">
                                {user.first_name && user.last_name
                                  ? `${user.first_name} ${user.last_name}`
                                  : user.username}
                              </p>
                              <p className="text-sm text-muted-foreground">{user.email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-mono">{formatInt(user.message_count)}</TableCell>
                          <TableCell className="text-right font-mono">{formatInt(user.session_count)}</TableCell>
                          <TableCell className="text-right font-mono">{formatInt(user.total_input_tokens)}</TableCell>
                          <TableCell className="text-right font-mono">{formatInt(user.total_output_tokens)}</TableCell>
                          <TableCell className="text-right font-mono">{formatNumber(user.avg_tokens_per_message)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(user.avg_cost_per_message)}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(user.total_cost)}</TableCell>
                          <TableCell className="text-center">
                            <AdminPanelButton
                              type="button"
                              size="sm"
                              onClick={() => fetchUserSessions(user.user_id, user.email)}
                              disabled={sessionsLoading}
                            >
                              <Eye className="h-4 w-4" />
                              View
                            </AdminPanelButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {sessionsDialogOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-hidden"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSessionsDialogOpen(false)
          }}
        >
          <div
            className="w-full max-w-6xl h-[63vh] bg-white dark:bg-gray-900 rounded-lg shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Session-wise token usage — {selectedUserEmail}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Sessions with tracked assistant messages
                  </p>
                </div>
                <AdminPanelButton
                  type="button"
                  size="icon"
                  onClick={() => setSessionsDialogOpen(false)}
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </AdminPanelButton>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {sessionsLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading session data…</div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No session data.</div>
              ) : (
                <div className="space-y-4">
                  <div className="relative overflow-x-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-white dark:bg-gray-900 z-10 [&_tr]:border-b">
                        <TableRow>
                          <TableHead className="bg-white dark:bg-gray-900">Session</TableHead>
                          <TableHead className="bg-white dark:bg-gray-900">Created</TableHead>
                          <TableHead className="text-right bg-white dark:bg-gray-900">In</TableHead>
                          <TableHead className="text-right bg-white dark:bg-gray-900">Out</TableHead>
                          <TableHead className="text-right bg-white dark:bg-gray-900">Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sessions.map((session) => (
                          <TableRow key={session.session_id}>
                            <TableCell className="font-medium">{session.session_name}</TableCell>
                            <TableCell>
                              {session.created_at ? formatISTDate(new Date(session.created_at)) : '—'}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatInt(session.total_input_tokens)}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatInt(session.total_output_tokens)}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(session.total_cost)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="border-t pt-4 grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-muted-foreground">Total input</p>
                      <p className="text-lg font-semibold font-mono">
                        {formatInt(sessions.reduce((s, x) => s + x.total_input_tokens, 0))}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total output</p>
                      <p className="text-lg font-semibold font-mono">
                        {formatInt(sessions.reduce((s, x) => s + x.total_output_tokens, 0))}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total cost</p>
                      <p className="text-lg font-semibold font-mono">
                        {formatCurrency(sessions.reduce((s, x) => s + x.total_cost, 0))}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
